import type { Call } from '@bcio-voice/contracts';
import { useRef, useState } from 'react';
import { Phone, PhoneIncoming, PhoneOff } from 'lucide-react';
import { useWorkspace, type WorkspaceValue } from '../../state/workspace.tsx';
import { controlGate } from '../../call/gating.ts';
import { isIncomingFor } from '../../call/callStore.ts';
import { hasPermission } from '../../lib/capabilities.ts';
import { Link } from '../../lib/router.tsx';
import { formatPhone } from '../../lib/format.ts';
import { Icon } from '../../ui/Icon.tsx';
import { armCallWindowAnswer } from './CallWindowDock.tsx';
import { partyName } from './callView.ts';

/** Same verified offer / readiness gate as the dock; visible reasons replace an unexplained disabled Answer. */
export function incomingControls(call: Call, ws: WorkspaceValue) {
  const permitted = !ws.session.view_as && hasPermission(ws.permissions, 'calls:control_own')
    && call.participants.some(p => p.user_id === ws.userId && !p.left_at);
  const ctx = { pending: ws.callControl.pending(call), readiness: ws.readiness, engineCall: ws.callControl.engineCall(call) };
  const answer = controlGate(call, 'answer', ctx);
  const reject = controlGate(call, 'reject', ctx);
  return {
    canAnswer: permitted && answer.enabled,
    canDecline: permitted && reject.enabled,
    reason: !permitted ? 'This call is read-only in this view.' : answer.enabled ? null
      : answer.reason ?? 'This call is no longer available to answer.',
  };
}

/** Mounted above the workspace on every route, including Phone: call waiting cannot fall below the live card. */
export function IncomingCallBanner() {
  const ws = useWorkspace();
  const call = ws.liveCalls.find(c => isIncomingFor(c, ws.userId));
  const [answering, setAnswering] = useState<string | null>(null);
  const pressed = useRef(false);
  if (!call || ws.session.view_as) return null;
  const controls = incomingControls(call, ws);
  const name = partyName(call, ws.userId, ws.userName);
  const status = ws.callControl.status(call);
  const answer = async () => {
    if (pressed.current || !controls.canAnswer) return;
    pressed.current = true;
    setAnswering(call.id);
    armCallWindowAnswer(ws, call.id);
    try { await ws.callControl.act(call, 'answer'); }
    finally { pressed.current = false; setAnswering(null); }
  };
  return <section className="call-bar surface-line call-bar-incoming incoming-call-banner" role="region" aria-label="Incoming call"
    style={{ display: 'flex', flexWrap: 'wrap', padding: '12px 16px', gap: '10px 16px', animation: 'none' }}>
    <Icon icon={PhoneIncoming} />
    <div style={{ flex: '1 1 180px', minWidth: 0 }}>
      <p style={{ margin: 0, fontWeight: 650, overflowWrap: 'anywhere' }}>Incoming call: {name}</p>
      {call.direction === 'inbound' && call.from.e164 ? <p className="meta" style={{ margin: 0 }}>{formatPhone(call.from.e164)}</p> : null}
      {status?.text || controls.reason ? <p className="inline-reason" role="status" style={{ margin: '4px 0 0' }}>{status?.text ?? controls.reason}</p> : null}
    </div>
    <div role="group" aria-label="Answer or decline incoming call" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
      <button type="button" className="btn btn-live" style={{ minHeight: 44 }} disabled={!controls.canAnswer || answering === call.id}
        onClick={() => void answer()} aria-busy={answering === call.id || undefined} aria-keyshortcuts="Alt+Shift+A">
        <Icon icon={Phone} /> {answering === call.id ? 'Answering...' : 'Answer'}
      </button>
      <button type="button" className="btn btn-end" style={{ minHeight: 44 }} disabled={!controls.canDecline}
        onClick={() => void ws.callControl.act(call, 'reject')} aria-keyshortcuts="Alt+Shift+E"><Icon icon={PhoneOff} /> Decline</button>
      <Link to="/phone" className="btn btn-quiet">Open phone</Link>
    </div>
  </section>;
}
